import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Icon, Radio, Result, Row, Spin, Typography,
} from 'antd';
import * as PropTypes from 'prop-types';
import moment from 'moment';
import {
  DASHBOARD_DOUGHNUT_COLOUR1,
  DASHBOARD_DOUGHNUT_COLOUR2,
  DASHBOARD_DOUGHNUT_COLOUR3,
  DASHBOARD_GRAPH_PIE_BORDER_COLOUR, doughnutColourArray,
} from '../dashboard-util';
import routeLocations from '../../../../utility/route-locations';
import { setLoadingParameterValues } from '../../../../state/ui/inspections/inspectionListUiActions';
import NoInspections from './NoInspections';
import {
  addDaysToJSDate,
  addYearsToJSDate,
  getDateComparisonNumber,
  getDateRangeForDashboardWidgetOption,
  isTimePeriodMovingUp
} from "../../bynx/packageUtil";

const { Text } = Typography;

// Graph dummy data
const dummyData = {
  labels: ['Compliance', 'DVLA', 'Other'],
  datasets: [{
    data: [300, 50, 100],
    borderColor: DASHBOARD_GRAPH_PIE_BORDER_COLOUR,
    backgroundColor: [
      DASHBOARD_DOUGHNUT_COLOUR1,
      DASHBOARD_DOUGHNUT_COLOUR2,
      DASHBOARD_DOUGHNUT_COLOUR3,
    ],
    hoverBackgroundColor: [
      DASHBOARD_DOUGHNUT_COLOUR1,
      DASHBOARD_DOUGHNUT_COLOUR2,
      DASHBOARD_DOUGHNUT_COLOUR3],
  }],
};

function renderDummyGraph() {
  return (
    <>
      <Doughnut data={dummyData} height={295} width={450} />
      <Row type="flex" justify="center" style={{ marginTop: '1em' }}>
        <Radio.Group size="small">
          <Radio.Button value={1}><FormattedMessage id="widgetOptionsDay" /></Radio.Button>
          <Radio.Button value={7} checked><FormattedMessage id="widgetOptionsDays" /></Radio.Button>
          <Radio.Button value={2}><FormattedMessage id="widgetOptionsFortnight" /></Radio.Button>
          <Radio.Button value={4}><FormattedMessage id="widgetOptionsWeeks" /></Radio.Button>
          <Radio.Button value={12}><FormattedMessage id="widgetOptionsYear" /></Radio.Button>
        </Radio.Group>
      </Row>
    </>
  );
}


export default function DashGraphInspectionBreakdown({
  history, dashboardData, widgetSettings, updateWidgetNumbers, changeData, localLoading
}) {
  // Redux
  const dispatch = useDispatch();
  const setLoadFilters = paramObj => dispatch(setLoadingParameterValues(paramObj));

  // Real graph data
  const [labels, setLabels] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [series1, setSeries1] = useState([]);
  const [option, setOption] = useState(7);
  const [noDataForOption, setNoDataForOption] = useState(false);
  const [dates, setDates] = useState(null);

  const graph1Data = {
    labels,
    datasets: [{
      data: series1,
      borderColor: DASHBOARD_GRAPH_PIE_BORDER_COLOUR,
      backgroundColor: doughnutColourArray,
      hoverBackgroundColor: doughnutColourArray,
    }],
  };

  // Option load
  useEffect(() => {
    if (widgetSettings && widgetSettings.length > 0) {
      setOption(widgetSettings[3]);
    }
  }, [widgetSettings, setOption]);

  // Load data
  useEffect(() => {
    // Today's date
    const todayDate = new Date();

    setSeries1([]);
    const reasonCodes = [];
    const reasonDescs = [];
    const inspections = [];

    if (dashboardData && dashboardData.reasonsByDate && dashboardData.reasonsByDate.length > 0) {
      if (option === 1 || option === 2 || option === 4 || option === 7) {
        // 1 day or 7 days or 2 weeks or 4 weeks
        let days = 1;
        if (option === 2) {
          days = 14;
        }
        if (option === 4) {
          days = 28;
        }
        if (option === 7) {
          days = 7;
        }
        const aDate = addDaysToJSDate(todayDate, (-days + 1)); // including today
        dashboardData.reasonsByDate.filter(row => {
          let loopQueryDate = row.submittedQueryDate;
          return loopQueryDate >= getDateComparisonNumber(aDate);
        }).forEach(row => {
          const idx = reasonCodes.indexOf(row.reasonCode);
          if (idx === -1) {
            reasonCodes.push(row.reasonCode);
            reasonDescs.push(row.reasonDescription);
            inspections.push(row.inspectionCount);
          } else {
            inspections[idx] += row.inspectionCount;
          }
        });
        setDates([moment(aDate).valueOf(), moment(todayDate).valueOf()]);
      } else if (option === 12) {
        // 12 months (all)
        let aDate = addYearsToJSDate(todayDate, -1);
        aDate = addDaysToJSDate(aDate, 1);
        dashboardData.reasonsByDate.filter(row => {
          let loopQueryDate = row.submittedQueryDate;
          return loopQueryDate >= getDateComparisonNumber(aDate);
        }).forEach(row => {
          const idx = reasonCodes.indexOf(row.reasonCode);
          if (idx === -1) {
            reasonCodes.push(row.reasonCode);
            reasonDescs.push(row.reasonDescription);
            inspections.push(row.inspectionCount);
          } else {
            inspections[idx] += row.inspectionCount;
          }
        });
        setDates([moment(todayDate).subtract(1, 'years').add(1, 'days').valueOf(), moment(todayDate).valueOf()]);
      }
    }

    setDescriptions(reasonDescs);
    setLabels(reasonDescs);
    setSeries1(inspections);
    setNoDataForOption(!(inspections && inspections.length > 0));

  }, [dashboardData, option, noDataForOption]);

  // Click through
  const clickOnSlice = (element) => {
    // Filters
    const filterObj = {
      my: false,
      compliance: false,
      other: false,
      defects: false,
      noDefects: false,
      faults: false,
      damages: false,
      costs: false,
      dates,
    };

    // Go to list
    if (descriptions && descriptions.length > 0 && element && element.length > 0) {
      setLoadFilters(filterObj);
      // eslint-disable-next-line no-underscore-dangle
      history.push(`${routeLocations.inspectionList}/${descriptions[element[0]._index]}`);
    }
  };

  // Change option and data
  const changeToNewOption = (newOption) => {
    // Must get data if moving up (already have if moving down)
    if (isTimePeriodMovingUp(option, newOption)) {
      const dateRange = getDateRangeForDashboardWidgetOption(newOption);
      changeData(dateRange[0], dateRange[1]);  //localLoading will be true during this
    }
    // Set new option
    setOption(newOption);
  };

  // No inspections (show dummy graph plus message)
  const hasInspections = dashboardData && dashboardData.inspectionStatistics && dashboardData.inspectionStatistics.totalInspections > 0;
  if (!hasInspections) return <NoInspections underlayComponent={renderDummyGraph} />;

  // GRAPH and switchers
  return (
    <div style={{ position: 'relative' }}>
      <Spin spinning={localLoading}>
      <Doughnut
        data={graph1Data}
        height={295}
        width={450}
        onElementsClick={clickOnSlice}
        options={{
          hover: {
            onHover(e) {
              // eslint-disable-next-line react/no-this-in-sfc
              const point = this.getElementAtEvent(e);
              if (point.length) e.target.style.cursor = 'pointer';
              else e.target.style.cursor = 'default';
            },
          },
          legend: {
            onHover(e) {
              e.target.style.cursor = 'pointer';
            },
          },
        }}
      />
      { noDataForOption
        ? (
          <div style={{
            position: 'absolute',
            top: '59px',
            width: '100%',
            height: 300,
          }}
          >
            <Result
              icon={<Icon type="carry-out" theme="twoTone" />}
              title={<FormattedMessage id="widgetMessageTitle" />}
              style={{ marginTop: '-2.5em' }}
              extra={(
                <div style={{ fontWeight: 500 }}>
                  <Row><Text><FormattedMessage id="widgetMessage4" /></Text></Row>
                </div>
            )}
            />
          </div>
        )
        : <></>}
      <Row type="flex" justify="center" align="middle" style={{ marginTop: '1.25em' }}>
        <Radio.Group
          size="small"
          value={option}
          onChange={e => {
            changeToNewOption(e.target.value);
            updateWidgetNumbers(widgetSettings[0], widgetSettings[1], widgetSettings[2], e.target.value, widgetSettings[4]);
          }}
        >
          <Radio.Button value={1}><FormattedMessage id="widgetOptionsDay" /></Radio.Button>
          <Radio.Button value={7}><FormattedMessage id="widgetOptionsDays" /></Radio.Button>
          <Radio.Button value={2}><FormattedMessage id="widgetOptionsFortnight" /></Radio.Button>
          <Radio.Button value={4}><FormattedMessage id="widgetOptionsWeeks" /></Radio.Button>
          <Radio.Button value={12}><FormattedMessage id="widgetOptionsYear" /></Radio.Button>
        </Radio.Group>
      </Row>
      </Spin>
    </div>
  );
}

DashGraphInspectionBreakdown.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  history: PropTypes.oneOfType([PropTypes.object]),
  changeData: PropTypes.func.isRequired,
  localLoading: PropTypes.bool.isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

DashGraphInspectionBreakdown.defaultProps = {
  dashboardData: null,
  history: null,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};
